import TrashIcon from './../../assets/icons/trash.svg';
import PenIcon from './../../assets/icons/pen.svg';
import { formatDate } from '../../utils';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Button,
  Popconfirm,
  Card,
  Spin,
  Tooltip,
  Tag,
  Row,
  Col,
  Typography,
  Tabs,
  Drawer,
  Space,
  DatePicker,
  Select,
  Input,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { getTickets, deleteTicket } from './Service/TicketService';
import NewTicketDrawer from './NewTicketDrawer';
import EditTicketDrawer from './EditTicketDrawer';
import { Ticket } from './Type/Ticket';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const TicketList: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(false);

  // Tab filtresi: "OPEN" veya "CLOSED"
  const [statusFilter, setStatusFilter] = useState<string>('OPEN');

  // Client-Side Pagination State
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Filters/search state (no user filter in this view)
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedPriority, setAppliedPriority] = useState<'LOW'|'MEDIUM'|'HIGH'|null>(null);
  const [appliedDateRange, setAppliedDateRange] = useState<[any, any] | null>(null);
  const [pPriority, setPPriority] = useState<'LOW'|'MEDIUM'|'HIGH'|null>(null);
  const [pDateRange, setPDateRange] = useState<[any, any] | null>(null);
  const [quickSearch, setQuickSearch] = useState<string>('');
  const [rawTickets, setRawTickets] = useState<Ticket[]>([]);

  const location = useLocation();

  // Query parametreleri parse eden yardımcı
  const getQueryParams = (search: string) => {
    return new URLSearchParams(search);
  };

  /**
   * status varsa ?status=OPEN / ?status=CLOSED ekleyerek sunucuya istek atar
   * Tüm veriyi çeker => tickets state'ine atar
   */
  const fetchTickets = async (status?: string, override?: { priority?: 'LOW'|'MEDIUM'|'HIGH'|null; dateRange?: [any, any] | null; search?: string }) => {
    setDrawerVisible(false);
    setLoading(true);
    try {
      // Build filters per backend spec
      const filters: string[] = [];
      const stat = status ?? statusFilter;
      if (stat) filters.push(`status.equals=${encodeURIComponent(stat)}`);
      // If override contains the key, use it even if null; else fall back to applied state
      const effPriority = (override && Object.prototype.hasOwnProperty.call(override, 'priority'))
        ? override.priority!
        : appliedPriority;
      const effDateRange = (override && Object.prototype.hasOwnProperty.call(override, 'dateRange'))
        ? override.dateRange!
        : appliedDateRange;
      if (effPriority) filters.push(`priority.equals=${effPriority}`);
      if (effDateRange && effDateRange[0] && effDateRange[1]) {
        const from = dayjs(effDateRange[0]).startOf('day').toDate().toISOString();
        const to = dayjs(effDateRange[1]).endOf('day').toDate().toISOString();
        filters.push(`createdAt.greaterThanOrEqual=${encodeURIComponent(from)}`);
        filters.push(`createdAt.lessThanOrEqual=${encodeURIComponent(to)}`);
      }
      const searchTerm = (override?.search || '').trim();
      if (searchTerm.length >= 2) filters.push(`search=${encodeURIComponent(searchTerm)}`);
      const paramStr = filters.length ? `?${filters.join('&')}` : '';
      const data: Ticket[] = await getTickets(paramStr);
      // Tarihe göre sıralama (en yeni üste)
      const sorted = data.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setRawTickets(sorted);
      setTickets(sorted);
      setPagination((prev) => ({ ...prev, current: 1 }));
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  // İlk açılışta sadece OPEN göster
  useEffect(() => {
    fetchTickets('OPEN');

    const queryParams = getQueryParams(location.search);
    const paramParametres: any = queryParams.get('parametres');
    if (paramParametres === 'help') {
      setDrawerVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced API search when user stops typing (>=2 chars), and debounced reset on clear
  const debouncedTicketSearch = React.useCallback(
    (function(){
      const fn = (term: string, status: string, prio: any, range: any) => {
        fetchTickets(status, { priority: prio, dateRange: range, search: term });
      };
      // simple debounce
      let t: any;
      return (term: string, status: string, prio: any, range: any) => {
        clearTimeout(t);
        t = setTimeout(() => fn(term, status, prio, range), 500);
      };
    })(),
    [fetchTickets]
  );
  useEffect(() => {
    const term = (quickSearch || '').trim();
    if (term.length >= 2) {
      debouncedTicketSearch(term, statusFilter, appliedPriority, appliedDateRange);
    } else if (term.length === 0) {
      // reset without search
      debouncedTicketSearch('', statusFilter, appliedPriority, appliedDateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickSearch, statusFilter, appliedPriority, appliedDateRange]);

  /**
   * Tab değiştiğinde (OPEN/CLOSED)
   */
  const handleTabChange = (key: string) => {
    // Reset all except user filter (no user filter here)
    setStatusFilter(key);
    setQuickSearch('');
    setAppliedPriority(null);
    setAppliedDateRange(null);
    setPPriority(null);
    setPDateRange(null);
    setFilterOpen(false);
    fetchTickets(key, { priority: null, dateRange: null, search: '' });
  };

  /**
   * Silme işlemi
   */
  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      await deleteTicket(id);
      await fetchTickets(statusFilter);
    } catch (error) {
      console.error('Failed to delete ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Öncelik ve Durum Tag rengi
   */
  const translatePriority = (priority: string) => {
    switch (priority) {
      case 'LOW':
        return <Tag color="green">Düşük</Tag>;
      case 'MEDIUM':
        return <Tag color="orange">Orta</Tag>;
      case 'HIGH':
        return <Tag color="red">Yüksek</Tag>;
      case 'OPEN':
        return <Tag color="orange">Açık</Tag>;
      case 'CLOSED':
        return <Tag color="green">Kapalı</Tag>;
      default:
        return null;
    }
  };

  // Tablo sütunları
  const columns = [
    {
      title: 'No',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (text: number) => <b>#{text}</b>,
    },
    {
      title: 'Konu',
      dataIndex: 'subject',
      key: 'subject',
      width: 250,
      ellipsis: true as any,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ display: 'inline-block', maxWidth: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      ellipsis: true as any,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ display: 'inline-block', maxWidth: 250, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Öncelik',
      dataIndex: 'priority',
      key: 'priority',
      render: (text: string) => translatePriority(text),
    },
    {
      title: 'Oluşturma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => formatDate(text),
    },
    {
      title: '',
      key: 'action',
      render: (text: any, record: Ticket) => (
        <Button
          type="default"
          className="d-button action-button"
          style={{ width: 100 }}
          onClick={() => {
            setSelectedTicket(record);
            setDrawerVisible(true);
          }}
        >Detay</Button>
      ),
    },
  ];

  // Ant Design Table onChange => pagination
  const handleTableChange = (newPagination: any) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  // Client-side pagination için slice
  const startIndex = (pagination.current - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const slicedData = tickets.slice(startIndex, endIndex);

  return (
    <div className="ticket-list-container mb-120px">
      <Tabs
        defaultActiveKey="OPEN"
        activeKey={statusFilter}
        onChange={handleTabChange}
        className="custom-tabs"
      >
        <Tabs.TabPane tab={
          <Button
            type={statusFilter === 'OPEN' ? 'primary' : 'default'}
            className="d-button tab-button"
          >
            Açık Talepler
          </Button>
        } key="OPEN" />
        <Tabs.TabPane tab={
          <Button
            type={statusFilter === 'CLOSED' ? 'primary' : 'default'}
            className="d-button tab-button"
          >
            Kapalı Talepler
          </Button>
        } key="CLOSED" />
      </Tabs>
      <Card className="br-30px">
        <Row gutter={[16, 16]} align="middle" justify="space-between" className="mb-20px">
          <Col xs={24} sm={12} lg={16}>
            <Row align="bottom">
              <Title level={4} style={{ margin: 0 }}>
                {statusFilter === "OPEN" ? "Açık ": "Kapalı "} Destek Taleplerim
              </Title>
              <Text type="secondary" className="ms-5">
                <strong>({tickets.length})</strong> kayıt listelendi
              </Text>
            </Row>
          </Col>
          <Col xs={24} sm={12} lg={8} style={{ textAlign: 'right' }} className="d-flex justify-content-end">
            <Space>
              <Input
                allowClear
                placeholder='Ara'
                className='d-input d-input-filter'
                style={{ width: 240 }}
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              />
              <Button type="primary" onClick={()=>{ setPPriority(appliedPriority); setPDateRange(appliedDateRange); setFilterOpen(true); }}>Filtrele</Button>
              <Button
                type="primary"
                className="dark-button d-button width-auto"
                onClick={() => { setSelectedTicket(null); setDrawerVisible(true); }}
              >
                Yeni Destek Talebi Oluştur
              </Button>
            </Space>
          </Col>
        </Row>

        <Spin spinning={loading}>
          <div style={{ overflowX: 'auto' }}>
            <Table
              columns={columns}
              dataSource={slicedData}
              rowKey="id"
              scroll={{ x: 'max-content' }}
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: tickets.length,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '20', '50'],
              }}
              onChange={handleTableChange}
            />
          </div>
        </Spin>

        {selectedTicket ? (
          <EditTicketDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            ticketId={selectedTicket.id}
            onSave={() => fetchTickets(statusFilter)}
            initialValues={selectedTicket}
          />
        ) : (
          <NewTicketDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            onSave={() => fetchTickets(statusFilter)}
            initialValues={{ priority: 'LOW' }}
          />
        )}
        {/* Filter Drawer */}
        <Drawer
          title='Filtre'
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          destroyOnClose
          width={420}
        >
          <Space direction='vertical' size='large' style={{ width: '100%' }}>
            <div>
              <Typography.Text>Öncelik</Typography.Text>
              <Select
                allowClear
                placeholder='Seçiniz'
                style={{ width: '100%', marginTop: 6 }}
                value={pPriority as any}
                onChange={(v) => setPPriority((v as any) as ('LOW'|'MEDIUM'|'HIGH'|null))}
                options={[{value:'LOW',label:'Düşük'},{value:'MEDIUM',label:'Orta'},{value:'HIGH',label:'Yüksek'}]}
              />
            </div>
            <div>
              <Typography.Text>Tarih</Typography.Text>
              <DatePicker.RangePicker
                style={{ width: '100%', marginTop: 6 }}
                value={pDateRange as any}
                onChange={(vals) => setPDateRange(vals as any)}
                format={'DD/MM/YYYY'}
              />
            </div>
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setPPriority(null);
                  setPDateRange(null);
                  setAppliedPriority(null);
                  setAppliedDateRange(null);
                  fetchTickets(statusFilter, { priority: null, dateRange: null, search: quickSearch });
                }}>Temizle</Button>
                <Button type='primary' onClick={() => {
                  setAppliedPriority(pPriority);
                  setAppliedDateRange(pDateRange);
                  setFilterOpen(false);
                  fetchTickets(statusFilter, { priority: pPriority, dateRange: pDateRange, search: quickSearch });
                }}>Filtrele</Button>
              </Space>
            </div>
          </Space>
        </Drawer>
      </Card>
    </div>
  );
};

export default TicketList;
